import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_construct-compliance/artifacts/jecks2sa_image.png";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-us" },
  { label: "About", href: "#about" },
  { label: "Clients", href: "#testimonials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[hsl(225_45%_7%/0.75)] backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-screen-2xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
        <button onClick={() => go("body")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO} alt="Qonnect QA" className="h-10 w-10 rounded-lg object-cover" />
          <span className="font-heading font-semibold tracking-tight text-lg text-foreground">
            Qonnect<span className="text-primary"> QA</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 relative group"
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#contact")}
            className="hidden md:inline-flex items-center rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 hover:scale-[1.03] active:scale-95 transition-transform duration-200"
            data-testid="nav-cta"
          >
            Get in touch
          </button>
          <button
            className="md:hidden text-foreground p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[hsl(225_45%_7%/0.95)] backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-base text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold"
                data-testid="mobile-nav-cta"
              >
                Get in touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
